import { useState, useEffect, useCallback } from 'react';
import { Plus, History, MessageSquare, Bot } from 'lucide-react';
import { financialSummaryApi } from '../../services/financialSummaryApi';
import { AgentChat } from '../shared/AgentChat';

export function ReportChat({
  threadId: externalThreadId,
  selectedCompanies,
  onSelectCompany,
  onRemoveCompany,
  onThreadCreated,
  onNewChat,
  onReportPage,
  onReportComplete,
}) {
  const [threads, setThreads] = useState([]);
  const [showThreads, setShowThreads] = useState(false);
  const [refreshThreads, setRefreshThreads] = useState(0);

  // Fetch thread history
  useEffect(() => {
    financialSummaryApi.getThreads()
      .then(data => setThreads(data.threads || []))
      .catch(() => setThreads([]));
  }, [refreshThreads]);

  const handleNewChat = () => {
    if (onNewChat) onNewChat();
  };

  // Handle custom SSE events
  const handleCustomEvent = useCallback((eventType, data, updateLast) => {
    if (eventType === 'report_page') {
      if (onReportPage) onReportPage(data.data || data);
      return true;
    }
    if (eventType === 'done') {
      if (onReportComplete) onReportComplete();
      return false; // let default handle done too
    }
    return false;
  }, [onReportPage, onReportComplete]);

  // Wrap onThreadCreated to also refresh thread list
  const handleThreadCreated = useCallback((tid) => {
    if (onThreadCreated) onThreadCreated(tid);
    setRefreshThreads(r => r + 1);
  }, [onThreadCreated]);

  const header = (
    <>
      {/* Top bar */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-zinc-200 shrink-0">
        <button onClick={handleNewChat} className="flex items-center gap-1.5 text-xs font-medium text-zinc-700 hover:text-zinc-900 transition-colors">
          <Plus size={14} />
          <span>New Report</span>
        </button>
        {threads.length > 0 && (
          <button onClick={() => setShowThreads(!showThreads)} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-700 transition-colors">
            <History size={14} />
          </button>
        )}
      </div>
      {/* Thread history */}
      {showThreads && threads.length > 0 && (
        <div className="border-b border-zinc-200 max-h-40 overflow-y-auto">
          {threads.map(t => (
            <button
              key={t.thread_id}
              onClick={() => { if (onThreadCreated) onThreadCreated(t.thread_id); setShowThreads(false); }}
              className={`w-full text-left flex items-center gap-1.5 px-4 py-2 text-xs transition-colors ${
                externalThreadId === t.thread_id ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700'
              }`}
            >
              <MessageSquare size={12} className="flex-shrink-0 opacity-50" />
              <span className="truncate">{t.title || 'Untitled'}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );

  const emptyState = (
    <>
      <Bot size={28} className="text-accent/30 mb-3" />
      <p className="text-sm text-zinc-400">Ask me to generate a financial summary or report.</p>
    </>
  );

  return (
    <AgentChat
      api={financialSummaryApi}
      agentName="Financial Summary"
      threadId={externalThreadId}
      onThreadCreated={handleThreadCreated}
      selectedCompanies={selectedCompanies}
      onSelectCompany={onSelectCompany}
      onRemoveCompany={onRemoveCompany}
      onCustomEvent={handleCustomEvent}
      header={header}
      emptyState={emptyState}
      placeholder="Describe the report you want…"
      compact
    />
  );
}
